import { Text } from "@clipboard-health/ui-react";
import { logError } from "@src/appV2/lib/analytics";
import {
  Player,
  PlayerError,
  PlayerEventType,
  PlayerState,
  create,
  isPlayerSupported,
} from "amazon-ivs-player";
/**
 * These imports are loaded via the file-loader, and return the path to the asset.
 * We use the TypeScript compiler (TSC) to check types; it doesn't know what this WASM module is.
 * We have also updated webpack.config.js file as per the amazon-ivs-player documentation
 * https://github.com/aws-samples/amazon-ivs-player-web-sample
 */
import wasmWorkerPath from "amazon-ivs-player/dist/assets/amazon-ivs-wasmworker.min.js";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import wasmBinaryPath from "amazon-ivs-player/dist/assets/amazon-ivs-wasmworker.min.wasm";
import { MutableRefObject, ReactElement, useEffect, useRef, useState } from "react";

function createAbsolutePath(assetPath: string): string {
  return new URL(assetPath, document.URL).toString();
}
interface IvsVideoPlayerProps {
  onVideoEventListener: (eventType: PlayerEventType | PlayerState) => void;
  streamUrl: string;
}
const ERROR_MESSAGE = "Error while playing the video, please try again.";

export function IvsVideoPlayer(props: IvsVideoPlayerProps): ReactElement {
  const { streamUrl, onVideoEventListener } = props;
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(!isPlayerSupported);

  const player: MutableRefObject<Player | null> = useRef(null);
  const videoElement: MutableRefObject<HTMLVideoElement | null> = useRef(null);

  useEffect(() => {
    player.current = create({
      wasmBinary: createAbsolutePath(wasmBinaryPath),
      wasmWorker: createAbsolutePath(wasmWorkerPath),
    });

    if (videoElement.current) {
      player.current.attachHTMLVideoElement(videoElement.current);
    }
    player.current.setAutoplay(true);
    player.current.load(streamUrl);
  }, [streamUrl]);

  useEffect(() => {
    const { ENDED, READY } = PlayerState;
    const { ERROR, INITIALIZED } = PlayerEventType;
    if (player.current) {
      const onInitializedListener = () => onVideoEventListener(INITIALIZED);
      const onReadyListener = () => onVideoEventListener(READY);
      const onEndedListener = () => onVideoEventListener(ENDED);
      const onErrorListener = (error: PlayerError) => {
        logError("Video Player Error", { error });
        setShowErrorMessage(true);
      };

      player.current.addEventListener(INITIALIZED, onInitializedListener);
      player.current.addEventListener(READY, onReadyListener);
      player.current.addEventListener(ENDED, onEndedListener);
      player.current.addEventListener(ERROR, onErrorListener);

      return () => {
        if (player.current) {
          player.current.removeEventListener(INITIALIZED, onInitializedListener);
          player.current.removeEventListener(READY, onReadyListener);
          player.current.removeEventListener(ENDED, onEndedListener);
          player.current.removeEventListener(ERROR, onErrorListener);
        }
        setShowErrorMessage(false);
      };
    }
  }, [onVideoEventListener]);

  if (showErrorMessage) {
    return (
      <Text textAlign="center" color="error">
        {ERROR_MESSAGE}
      </Text>
    );
  }

  return (
    <>
      {/** We are ignoring the accessibility needs of our users and breaking the law here.
       * We are ignoring the accessibility lint rule here that requires media files to have
       * captions/subtitles. For example, the facility videos don't include subtitles
       * FIXME: Make this comply with accessibility laws by requiring subtitle data **/}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        className="ivs-video"
        playsInline
        ref={videoElement}
        controls
        width="100%"
        height="100%"
      ></video>
    </>
  );
}
